import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { useAuth } from "../auth";
import { loadMapSnapshot, mapLabelNode, type MapSnapshot } from "./mapSnapshot";

function driverIcon(status: string): L.DivIcon {
  const color = status === "available" ? "#22c55e" : status === "on_route" ? "#38bdf8" :
    status === "busy" ? "#f59e0b" : "#64798d";
  return L.divIcon({ className: "map-pin",
    html: '<span class="pin-dot driver" style="background:' + color + '"></span>',
    iconSize: [16, 16], iconAnchor: [8, 8] });
}
const deliveryIcon = L.divIcon({ className: "map-pin",
  html: '<span class="pin-dot delivery"></span>', iconSize: [12, 12], iconAnchor: [6, 6] });

export default function MapView() {
  const { user } = useAuth();
  // A different operator or tenant never inherits prior locations.
  return user ? <ScopedMap key={user.shop_id + ":" + user.id} /> : <p>Sign in to read the fleet map.</p>;
}

function ScopedMap() {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const fitted = useRef(false);
  const [snapshot, setSnapshot] = useState<MapSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!elRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true }).setView([20, 0], 2);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: "© OpenStreetMap",
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    const timer = setTimeout(() => map.invalidateSize(), 120);
    return () => {
      clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      fitted.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | null = null;
    async function load() {
      controller = new AbortController();
      setLoading(true);
      try {
        const next = await loadMapSnapshot(controller.signal);
        if (!active) return;
        setSnapshot(next);
        setError(null);
      } catch (cause) {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : "The map could not be read.");
      } finally {
        if (active) {
          setLoading(false);
          // Schedule after completion; slow reads cannot accumulate polls.
          timer = setTimeout(() => void load(), 15000);
        }
      }
    }
    void load();
    return () => { active = false; controller?.abort(); if (timer) clearTimeout(timer); };
  }, [refresh]);

  useEffect(() => {
    const layer = layerRef.current;
    const map = mapRef.current;
    if (!snapshot || !layer || !map) return;
    layer.clearLayers();
    const positions: [number, number][] = [];
    for (const point of snapshot.points) {
      if (!point.position) continue;
      const observed = point.kind === "driver"
        ? " · Location observed: " + (point.locationAt ?? "unknown") : " · Delivery address, not vehicle position";
      L.marker(point.position, { icon: point.kind === "driver" ? driverIcon(point.status) : deliveryIcon })
        .bindTooltip(mapLabelNode(point.label + observed)).addTo(layer);
      positions.push(point.position);
    }
    // Do not undo an operator's deliberate pan on every poll.
    if (!fitted.current && positions.length) {
      map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], maxZoom: 14 });
      fitted.current = true;
    }
  }, [snapshot]);

  const points = snapshot?.points ?? [];
  const driverPins = points.filter((p) => p.kind === "driver" && p.position).length;
  const deliveryPins = points.filter((p) => p.kind === "delivery" && p.position).length;
  return (
    <div className="map-page">
      <div className="board-header">
        <h1>Fleet map</h1>
        <button type="button" disabled={loading} onClick={() => setRefresh((v) => v + 1)}>
          {loading ? "Reading locations…" : "Refresh locations"}
        </button>
      </div>
      <div role="status" aria-live="polite">
        {snapshot ? <>
          <p>{driverPins} driver locations / {snapshot.driverRows} returned drivers; {deliveryPins} delivery addresses / {snapshot.deliveryRows} returned deliveries.</p>
          <p>{error ? "Stale snapshot" : "Last successful read"}: {snapshot.receivedAt}. Driver location observation times are separate; a fresh read does not prove a fresh GPS fix.</p>
          <p>Queue, active and attention lanes; newest {snapshot.boardCap} maximum across those lanes.
            {snapshot.truncated ? " More deliveries exist beyond this view." : " The source did not report truncation."}
            {" "}Offline/inactive drivers and completed deliveries are not included. Missing or invalid coordinates stay in the list below.</p>
        </> : <p>{loading ? "Reading the current fleet and board…" : "No verified map snapshot is available."}</p>}
      </div>
      {error && <div className="banner-err" role="alert">{error} {snapshot ? "Previous locations are retained, not current confirmations." : "An empty fleet was not inferred."}</div>}
      <div className="map-canvas" ref={elRef} aria-label="Fleet locations and delivery addresses" />
      {snapshot && <details>
        <summary>Textual locations ({points.length} returned records)</summary>
        <p>Fleet and board are separate reads, not one atomic snapshot. Locations do not establish route feasibility or assignment authority.</p>
        <ul>{points.map((point) => <li key={point.kind + ":" + point.id}>
          {point.label} — {point.position ? point.position.join(", ") : "No usable coordinates"}.
          {point.kind === "driver" ? " Location observed: " + (point.locationAt ?? "unknown") + "." : " Delivery address, not vehicle position."}
        </li>)}</ul>
      </details>}
    </div>
  );
}
